import { Component } from '@theme/component';
import { debounce, preloadImage, prefersReducedMotion } from '@theme/utilities';
import { scrollIntoView } from '@theme/scrolling';
import { ZoomMediaSelectedEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';

export class ZoomDialog extends Component {
  requiredRefs = ['dialog', 'media', 'thumbnails'];
  #highResImagesLoaded = new Set();

  connectedCallback() {
    super.connectedCallback();
    this.refs.dialog.addEventListener('scroll', this.handleScroll);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.dialog.removeEventListener('scroll', this.handleScroll);
  }

  async open(index, event) {
    event?.preventDefault();
    const { dialog, media, thumbnails } = this.refs;
    if (!dialog || !media?.length) return;
    const safeIndex = Math.max(0, Math.min(Number(index) || 0, media.length - 1));
    if (!dialog.open) dialog.showModal();
    dialog.classList.remove('dialog--closed');
    this.selectThumbnail(safeIndex, { behavior: 'instant' });
    const targetImage = media[safeIndex];
    targetImage?.scrollIntoView({ behavior: 'instant', block: 'nearest', inline: 'nearest' });
    thumbnails?.children[safeIndex]?.scrollIntoView({ behavior: 'instant', block: 'nearest', inline: 'nearest' });
  }

  loadHighResolutionImage(mediaContainer) {
    if (!mediaContainer?.classList.contains('product-media-container--image')) return false;
    const image = mediaContainer.querySelector('img.product-media__image');
    if (!(image instanceof HTMLImageElement)) return false;
    const highResolutionUrl = image.getAttribute('data_max_resolution');
    if (!highResolutionUrl || this.#highResImagesLoaded.has(highResolutionUrl)) return false;
    preloadImage(highResolutionUrl);
    const newImage = new Image();
    newImage.className = image.className;
    newImage.alt = image.alt;
    newImage.setAttribute('data_max_resolution', highResolutionUrl);
    newImage.setAttribute('ref', 'image');
    newImage.onload = () => {
      if (image.isConnected) image.replaceWith(newImage);
      this.#highResImagesLoaded.add(highResolutionUrl);
    };
    newImage.src = highResolutionUrl;
    return true;
  }

  handleScroll = debounce(async () => {
    const { dialog, media, thumbnails } = this.refs;
    if (!dialog.open || !media?.length) return;
    const mostVisibleElement = await getMostVisibleElement(media);
    if (!dialog.open || !mostVisibleElement) return;
    const activeIndex = media.indexOf(mostVisibleElement);
    if (activeIndex < 0 || !thumbnails.children[activeIndex]) return;
    Array.from(thumbnails.querySelectorAll('button')).forEach((button, i) => {
      button.setAttribute('aria-selected', String(i === activeIndex));
    });
    this.loadHighResolutionImage(mostVisibleElement);
    this.dispatchEvent(new ZoomMediaSelectedEvent(activeIndex));
  }, 50);

  async close() {
    if (!this.refs.dialog?.open) return;
    this.refs.dialog.classList.remove('dialog--closed');
    this.closeDialog();
  }

  closeDialog() {
    const { dialog } = this.refs;
    if (!dialog?.open) return;
    dialog.close();
    window.dispatchEvent(new DialogCloseEvent());
  }

  next() { this.#navigate(1); }
  previous() { this.#navigate(-1); }

  #navigate(direction) {
    const buttons = Array.from(this.refs.thumbnails.querySelectorAll('button'));
    if (!buttons.length) return;
    const currentIndex = buttons.findIndex(button => button.getAttribute('aria-selected') === 'true');
    const nextIndex = ((currentIndex < 0 ? 0 : currentIndex) + direction + buttons.length) % buttons.length;
    this.selectThumbnail(nextIndex, { behavior: prefersReducedMotion() ? 'instant' : 'smooth' });
  }

  handleKeyDown(event) {
    switch (event.key) {
      case 'Escape': event.preventDefault(); this.close(); break;
      case 'ArrowRight': event.preventDefault(); this.next(); break;
      case 'ArrowLeft': event.preventDefault(); this.previous(); break;
    }
  }

  async handleThumbnailClick(index) {
    this.selectThumbnail(index, { behavior: prefersReducedMotion() ? 'instant' : 'smooth' });
  }

  async handleThumbnailPointerEnter(index) {
    const { media } = this.refs;
    if (media[index]) this.loadHighResolutionImage(media[index]);
  }

  async selectThumbnail(index, options = { behavior: 'smooth' }) {
    const { media, thumbnails } = this.refs;
    if (!thumbnails?.children?.length || !Number.isInteger(index) || index < 0 || index >= thumbnails.children.length) return;
    const targetThumbnail = thumbnails.children[index];
    if (!(targetThumbnail instanceof HTMLElement)) return;
    Array.from(thumbnails.querySelectorAll('button')).forEach((button, i) => {
      button.setAttribute('aria-selected', String(i === index));
    });
    scrollIntoView(targetThumbnail, {
      ancestor: thumbnails,
      behavior: options.behavior,
      block: 'center',
      inline: 'center',
    });
    const targetImage = media[index];
    if (targetImage) {
      targetImage.scrollIntoView({ behavior: options.behavior, block: 'nearest', inline: 'nearest' });
      this.loadHighResolutionImage(targetImage);
    }
    this.dispatchEvent(new ZoomMediaSelectedEvent(index));
  }
}

if (!customElements.get('zoom-dialog')) customElements.define('zoom-dialog', ZoomDialog);

function getMostVisibleElement(elements) {
  return new Promise(resolve => {
    if (!elements?.length) return resolve(null);
    const observer = new IntersectionObserver(entries => {
      const mostVisible = entries.reduce((a, b) => b.intersectionRatio > a.intersectionRatio ? b : a);
      observer.disconnect();
      resolve(mostVisible.target);
    }, { threshold: Array.from({ length: 21 }, (_, i) => i / 20) });
    elements.forEach(element => observer.observe(element));
  });
}